import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { Loader2, CheckCircle2, Edit3, Trash2, Calendar, AlertTriangle, Sparkles } from "lucide-react";

const STATUS_LABEL = {
  submitted: "Submitted — Needs Review",
  accepted_pending_pickup: "Accepted — Awaiting Pickup",
  pending_customer_review: "Awaiting Customer Re-Accept",
  customer_accepted: "Customer Accepted — Set Pickup",
  completed: "Completed",
};

export default function PurchaseRequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [showAccept, setShowAccept] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ pickup_date: "", pickup_window: "", note: "" });

  const [editMode, setEditMode] = useState(false);
  const [editItems, setEditItems] = useState([]); // [{inventory_id, quantity, keep, title, price, ...}]
  const [editNote, setEditNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/purchase-requests/${id}`);
      setPr(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const wrap = async (fn) => {
    setBusy(true); setError("");
    try { await fn(); await load(); }
    catch (e) { setError(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const accept = () => wrap(async () => {
    if (!acceptForm.pickup_date || !acceptForm.pickup_window) {
      setError("Pickup date + window required."); return;
    }
    await api.post(`/admin/purchase-requests/${id}/accept`, acceptForm);
    setShowAccept(false);
  });

  const beginEdit = () => {
    setEditItems(pr.items.map((it) => ({
      inventory_id: it.inventory_id,
      quantity: it.quantity || 1,
      keep: true,
      title: it.title, console: it.console, price: it.price,
      components: it.components, condition: it.condition,
    })));
    setEditNote("");
    setEditMode(true);
  };
  const submitEdit = () => wrap(async () => {
    const kept = editItems.filter((it) => it.keep);
    if (kept.length === 0) { setError("Keep at least one item — use Delete to remove the whole request."); return; }
    await api.post(`/admin/purchase-requests/${id}/edit`, {
      items: editItems.map((it) => ({ inventory_id: it.inventory_id, quantity: it.quantity, keep: it.keep })),
      note: editNote,
    });
    setEditMode(false);
  });

  const complete = async () => {
    if (!window.confirm("Complete this request? Items will be removed from inventory and the request archived to Closed.")) return;
    setBusy(true); setError("");
    try {
      const r = await api.post(`/admin/purchase-requests/${id}/complete`);
      const earned = Number(r.data?.points_earned || 0);
      const bonus = Number(r.data?.promo_bonus || 0);
      if (pr.member_phone && earned > 0) {
        const label = `${earned.toLocaleString()} µPoints credited to ${pr.customer_name}` + (bonus > 0 ? ` (incl. ${bonus.toLocaleString()} promo bonus)` : "");
        toast.success("Reservation completed", { description: label, duration: 7000 });
      } else if (pr.member_phone) {
        toast.success("Reservation completed", { description: `No µPoints — request total was $0.`, duration: 5000 });
      } else {
        toast.success("Reservation completed", { description: "Guest customer — no µPoints credited.", duration: 5000 });
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };
  const del = () => wrap(async () => {
    if (!window.confirm("Delete this request? Items will be released back to inventory.")) return;
    await api.delete(`/admin/purchase-requests/${id}`);
    nav("/pos/purchase-requests");
  });

  if (loading) return <Layout><div className="max-w-3xl mx-auto px-6 py-20 text-center"><Loader2 className="animate-spin mx-auto" /></div></Layout>;
  if (!pr) return <Layout><div className="max-w-3xl mx-auto px-6 py-20 text-center">{error || "Not found"}</div></Layout>;

  const canAccept = ["submitted", "customer_accepted", "accepted_pending_pickup"].includes(pr.status);
  const canEdit = pr.status !== "completed";
  const canComplete = pr.status === "accepted_pending_pickup";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => nav("/pos/purchase-requests")} className="label-eyebrow mb-6 inline-block" data-testid="pr-detail-back">← Back to Requests</button>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
          <h1 className="font-display text-3xl font-black">{pr.customer_name}</h1>
          <span className="font-mono text-xs px-2 py-1" style={{ background: "var(--ab-surface-2)", color: "var(--ab-text-dim)" }} data-testid="pr-detail-id">{pr.id.slice(0, 8)}</span>
        </div>
        <div className="text-sm mb-6" style={{ color: "var(--ab-text-muted)" }} data-testid="pr-detail-status">
          {STATUS_LABEL[pr.status] || pr.status}
        </div>

        {pr.pending_edits && pr.status === "pending_customer_review" && (
          <div className="surface p-4 mb-6 inline-flex items-start gap-2" style={{ borderLeft: "3px solid var(--ab-warning)" }} data-testid="pr-detail-pending-note">
            <AlertTriangle size={16} style={{ color: "var(--ab-warning)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <div className="label-eyebrow mb-1" style={{ color: "var(--ab-warning)" }}>Awaiting customer response</div>
              <p className="text-sm">You proposed an updated list. The customer has been emailed and must accept/decline before you can finalise.</p>
            </div>
          </div>
        )}

        <div className="surface p-5 mb-6">
          <div className="label-eyebrow mb-3">Customer</div>
          <div className="text-sm">{pr.customer_name} · <a href={`mailto:${pr.customer_email}`} className="hover:text-[var(--ab-primary)]">{pr.customer_email}</a></div>
          <div className="text-sm" style={{ color: "var(--ab-text-muted)" }}>{pr.customer_phone || "—"} {pr.member_phone ? " · AB Member" : " · Guest"}</div>
          {pr.notes && <div className="text-sm mt-2" style={{ color: "var(--ab-text-muted)" }}><b>Notes:</b> {pr.notes}</div>}
        </div>

        {!editMode ? (
          <div className="surface divide-y mb-6" style={{ borderColor: "var(--ab-border)" }} data-testid="pr-detail-items">
            {pr.items.map((it, i) => (
              <div key={i} className="flex justify-between items-center px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-bold">{it.title}</div>
                  <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.console} · {it.components || ""} · {it.condition || ""} · qty {it.quantity || 1}</div>
                </div>
                <div className="font-mono font-bold" style={{ color: "var(--ab-primary)" }}>${Number(it.price).toFixed(it.is_non_game ? 2 : 0)}</div>
              </div>
            ))}
            <div className="flex justify-between items-center px-5 py-4" style={{ background: "var(--ab-surface-2)" }}>
              <div className="text-xs space-y-0.5">
                <div className="flex justify-between gap-12"><span style={{ color: "var(--ab-text-muted)" }}>Subtotal</span><span className="font-mono">${Number(pr.subtotal || pr.total).toFixed(2)}</span></div>
                {pr.points_discount_amount > 0 && <div className="flex justify-between gap-12" style={{ color: "var(--ab-primary)" }}><span>µPoints ({Number(pr.points_redeemed || 0).toLocaleString()} pts)</span><span className="font-mono">−${Number(pr.points_discount_amount).toFixed(2)}</span></div>}
                {pr.trade_credit_used > 0 && <div className="flex justify-between gap-12" style={{ color: "var(--ab-primary)" }}><span>Trade credit</span><span className="font-mono">−${Number(pr.trade_credit_used).toFixed(2)}</span></div>}
              </div>
              <div className="text-right">
                <div className="label-eyebrow">Total</div>
                <div className="font-mono font-bold text-2xl" style={{ color: "var(--ab-primary)" }} data-testid="pr-detail-total">${Number(pr.total).toFixed(2)}</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="surface divide-y mb-6" style={{ borderColor: "var(--ab-border)" }} data-testid="pr-detail-edit">
            {editItems.map((it, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3" style={{ opacity: it.keep ? 1 : 0.45 }}>
                <input type="checkbox" checked={it.keep} onChange={(e) => setEditItems((p) => p.map((x, j) => j === i ? { ...x, keep: e.target.checked } : x))} data-testid={`pr-edit-keep-${i}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{it.title}</div>
                  <div className="text-xs" style={{ color: "var(--ab-text-dim)" }}>{it.console} · ${Number(it.price).toFixed(2)}</div>
                </div>
              </div>
            ))}
            <div className="px-5 py-4">
              <label className="label-eyebrow block mb-2">Note to customer (optional)</label>
              <textarea className="input-terminal" rows={3} value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="e.g. We sold the Goldeneye copy yesterday, sorry!" data-testid="pr-edit-note" />
            </div>
          </div>
        )}

        {pr.status === "accepted_pending_pickup" && pr.pickup_date && (
          <div className="surface p-5 mb-6" data-testid="pr-detail-pickup">
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Pickup</div>
            <div className="text-base"><b>{pr.pickup_date}</b> · {pr.pickup_window}</div>
            {pr.pickup_note && <p className="text-sm mt-2" style={{ color: "var(--ab-text-muted)" }}>{pr.pickup_note}</p>}
          </div>
        )}

        {pr.status === "completed" && (
          <div className="surface p-5 mb-6 inline-flex items-start gap-3" style={{ borderLeft: "3px solid var(--ab-primary)" }} data-testid="pr-detail-credited">
            <Sparkles size={18} style={{ color: "var(--ab-primary)", flexShrink: 0, marginTop: 2 }} />
            <div>
              <div className="label-eyebrow mb-1" style={{ color: "var(--ab-primary)" }}>Completed{pr.closed_at ? ` · ${new Date(pr.closed_at).toLocaleString()}` : ""}</div>
              {pr.member_phone ? (
                <p className="text-sm">
                  <b>{Number(pr.points_earned || 0).toLocaleString()} µPoints</b> credited to {pr.customer_name}
                  {pr.promo_bonus > 0 && <> (incl. <b>{Number(pr.promo_bonus).toLocaleString()}</b> promo bonus)</>}
                  · Lifetime spend updated by <b>${Number(pr.total).toFixed(2)}</b>.
                </p>
              ) : (
                <p className="text-sm" style={{ color: "var(--ab-text-muted)" }}>Guest customer — no µPoints credited.</p>
              )}
            </div>
          </div>
        )}

        {error && <div className="surface p-3 text-sm mb-4" style={{ color: "var(--ab-danger)" }} data-testid="pr-detail-error">{error}</div>}

        {/* Action toolbar */}
        {editMode ? (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setEditMode(false)} disabled={busy} className="btn-secondary" data-testid="pr-detail-edit-cancel">Cancel</button>
            <button onClick={submitEdit} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="pr-detail-edit-save">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Edit3 size={14} />} Send Updated List
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {canAccept && (
              <button onClick={() => setShowAccept(true)} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="pr-detail-accept">
                <Calendar size={14} /> Accept & Set Pickup
              </button>
            )}
            {canEdit && (
              <button onClick={beginEdit} disabled={busy} className="btn-secondary inline-flex items-center gap-2" data-testid="pr-detail-edit">
                <Edit3 size={14} /> Edit Items
              </button>
            )}
            <button onClick={complete} disabled={busy || !canComplete} className="btn-secondary inline-flex items-center gap-2" title={canComplete ? "" : "Press Accept & Set Pickup first."} data-testid="pr-detail-complete">
              <CheckCircle2 size={14} /> Complete
            </button>
            <button onClick={del} disabled={busy} className="btn-danger inline-flex items-center gap-2" data-testid="pr-detail-delete">
              <Trash2 size={14} /> Delete
            </button>
          </div>
        )}
      </div>

      {/* Accept modal */}
      {showAccept && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(4px)" }} data-testid="pr-accept-modal" onClick={() => !busy && setShowAccept(false)}>
          <div className="surface p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow mb-2" style={{ color: "var(--ab-primary)" }}>Set Pickup</div>
            <h3 className="font-display text-2xl font-bold mb-4">Accept Reservation</h3>
            <div className="space-y-3">
              <div>
                <label className="label-eyebrow block mb-2">Pickup Date</label>
                <input type="date" className="input-terminal" value={acceptForm.pickup_date} onChange={(e) => setAcceptForm((p) => ({ ...p, pickup_date: e.target.value }))} data-testid="pr-accept-date" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Pickup Window</label>
                <input className="input-terminal" placeholder="e.g. 2pm-5pm" value={acceptForm.pickup_window} onChange={(e) => setAcceptForm((p) => ({ ...p, pickup_window: e.target.value }))} data-testid="pr-accept-window" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Note (optional)</label>
                <textarea className="input-terminal" rows={2} value={acceptForm.note} onChange={(e) => setAcceptForm((p) => ({ ...p, note: e.target.value }))} data-testid="pr-accept-note" />
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button onClick={() => setShowAccept(false)} disabled={busy} className="btn-secondary" data-testid="pr-accept-cancel">Cancel</button>
              <button onClick={accept} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="pr-accept-submit">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Send Confirmation
              </button>
            </div>
            {error && <div className="mt-3 text-sm" style={{ color: "var(--ab-danger)" }}>{error}</div>}
          </div>
        </div>
      )}
    </Layout>
  );
}
